import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Edit3,
  Building2,
  Trash2,
  Calendar,
  User,
  Loader2,
  ShieldCheck,
  Award,
  Activity
} from 'lucide-react';
import Sidebar from '../../components/Dashboard/Sidebar/Sidebar';
import Header from '../../components/Dashboard/Header/Header';
import styles from '../../styles/MasterView.module.css';
import api from '../../api/axios';
import Breadcrumbs from '../../components/Common/Breadcrumbs/Breadcrumbs';
import Footer from '../../components/Common/Footer/Footer';
import toast from 'react-hot-toast';
import DepartmentDrawer from './DepartmentDrawer';
import NotFound from '../Error/NotFound';

import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';

const MySwal = withReactContent(Swal);

const ViewDepartment = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [department, setDepartment] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const fetchDepartment = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/departments/${id}`);
      if (response.data.success) {
        setDepartment(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching department details:', error);
      toast.error('Failed to load department details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDepartment();
  }, [id]);

  const handleDelete = () => {
    MySwal.fire({
      title: 'Dissolve Department?',
      text: `Are you sure you want to delete ${department.departement_name}? This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#94a3b8',
      confirmButtonText: 'Yes, dissolve it'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await api.delete(`/departments/${department.id}`);
          if (response.data.success) {
            toast.success('Department dissolved successfully');
            navigate('/employees/departments');
          }
        } catch (error) {
          toast.error(error.response?.data?.message || 'Error deleting department');
        }
      }
    });
  };

  if (loading) {
    return (
      <div className={styles['view-master-container']}>
        <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
        <div className={styles['content-wrapper']}>
          <Header onMenuClick={() => setIsSidebarOpen(true)} />
          <div className={styles['loading-container']}>
            <Loader2 size={40} className={styles['spinner']} />
            <p>Analyzing Office Structure...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!department) {
    return <NotFound />;
  }


  return (
    <div className={`${styles['view-master-container']} ${isSidebarOpen ? styles['sidebar-open'] : ''}`}>
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
      {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}

      <div className={styles['content-wrapper']}>
        <Header onMenuClick={() => setIsSidebarOpen(true)} />

        <main className={styles['main-content']}>
          <Breadcrumbs />

          {/* Page Header */}
          <div className={styles['page-header']}>
            <div className={styles['header-title-section']}>
              <h1>Department Details</h1>
              <p>Manage organizational hierarchy and horizontal resource allocation</p>
            </div>
            <div className={styles['header-actions-top']}>
              <button className={styles['btn-secondary']} onClick={() => navigate('/employees/departments')}>
                <ArrowLeft size={18} />
                <span>Back</span>
              </button>
              <button className={styles['btn-primary']} onClick={() => setIsDrawerOpen(true)}>
                <Edit3 size={18} />
                <span>Update Department</span>
              </button>

            </div>
          </div>

          {/* Master Profile Header Card */}
          <div className={styles['master-profile-header']}>
            <div className={styles['header-main']}>
              <div
                className={styles['avatar-large']}
                style={{ background: 'var(--primary-gradient)' }}
              >
                <Building2 size={32} />
              </div>
              <div className={styles['profile-basic-info']}>
                <div className={styles['title-flex']}>
                  <h1>{department.departement_name}</h1>
                  <span className={styles['status-badge']}>
                    <ShieldCheck size={14} /> ACTIVE DEPARTMENT
                  </span>
                </div>
                <div className={styles['master-meta']}>
                  <div className={styles['meta-item']}>
                    <Building2 size={16} />
                    <span>Classification: <strong>Organizational Unit</strong></span>
                  </div>
                  <div className={styles['meta-item']}>
                    <Calendar size={16} />
                    <span>Chartered On: <strong>{new Date(department.created_at).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}</strong></span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Metrics Row */}
          {/* <div className={styles['metrics-row']}>
            <div className={styles['metric-card']}>
              <div className={styles['m-header']}>
                <div className={`${styles['m-icon']} ${styles['bg-blue']}`}><User size={20} /></div>
              </div>
              <div className={styles['m-info']}>
                <label>Total Strength</label>
                <h2>28</h2>
              </div>
            </div>
            <div className={styles['metric-card']}>
              <div className={styles['m-header']}>
                <div className={`${styles['m-icon']} ${styles['bg-green']}`}><Activity size={20} /></div>
              </div>
              <div className={styles['m-info']}>
                <label>Active Projects</label>
                <h2>08</h2>
              </div>
            </div>
            <div className={styles['metric-card']}>
              <div className={styles['m-header']}>
                <div className={`${styles['m-icon']} ${styles['bg-orange']}`}><Award size={20} /></div>
              </div>
              <div className={styles['m-info']}>
                <label>Efficiency</label>
                <h2>92%</h2>
              </div>
            </div>
            <div className={styles['metric-card']}>
              <div className={styles['m-header']}>
                <div className={`${styles['m-icon']} ${styles['bg-slate']}`}><ShieldCheck size={20} /></div>
              </div>
              <div className={styles['m-info']}>
                <label>Compliance</label>
                <h2>Verified</h2>
              </div>
            </div>
          </div> */}

          {/* Dashboard Grid */}
          <div className={styles['dashboard-grid']}>
            <div className={styles['left-column']}>
              {/* Identification Details */}
              <div className={styles['standard-card']}>
                <div className={styles['card-header']}>
                  <div className={styles['card-icon']}><Building2 size={20} /></div>
                  <h3>Department Identification</h3>
                </div>
                <div className={styles['details-list']}>
                  <div className={styles['detail-row']}>
                    <div className={styles['detail-item']}>
                      <span className={styles['d-label']}>Official Department Name</span>
                      <span className={styles['d-value']}>{department.departement_name}</span>
                    </div>
                    <div className={styles['detail-item']}>
                      <span className={styles['d-label']}>Department Code</span>
                      <span className={styles['d-value']}>DEPT-0{department.id}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Departmental Activity */}
              {/* <div className={styles['standard-card']}>
                <div className={styles['card-header']}>
                  <div className={styles['card-icon']}><Activity size={20} /></div>
                  <h3>Resource Allocation Index</h3>
                </div>
                <div style={{ textAlign: 'center', padding: '40px 0', color: '#94a3b8' }}>
                  <p style={{ fontSize: '13px' }}>Historical staffing and project involvement logs for this department will be summarized here.</p>
                </div>
              </div> */}
            </div>

            <div className={styles['right-column']}>
              {/* Registry Info */}
              <div className={styles['standard-card']}>
                <div className={styles['card-header']}>
                  <div className={styles['card-icon']}><ShieldCheck size={20} /></div>
                  <h3>System Registry</h3>
                </div>
                <div className={styles['info-block']}>
                  <div className={styles['meta-sidebar-item']}>
                    <div className={styles['sidebar-icon']}><User size={18} /></div>
                    <div className={styles['sidebar-text']}>
                      <label>CHARTERED BY</label>
                      <span>{department.creator?.name || 'System Admin'}</span>
                    </div>
                  </div>
                  <div className={styles['meta-sidebar-item']}>
                    <div className={styles['sidebar-icon']}><Calendar size={18} /></div>
                    <div className={styles['sidebar-text']}>
                      <label>REGISTRATION DATE</label>
                      <span>{new Date(department.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <Footer />
        </main>
      </div>

      <DepartmentDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        department={department}
        onSave={fetchDepartment}
      />
    </div>
  );
};

export default ViewDepartment;
